import { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, ShieldCheck, ShieldOff, Smartphone } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BackupCodes, TwoFactorSetup } from "@/components/settings/TwoFactorSetup";
import { formatDateTime } from "@/lib/dateTime";
import { PhoneVisual } from "@/components/auth/AuthIllustrations";

interface Status {
  enabled: boolean;
  enabled_at: string | null;
  backup_codes_left: number;
  required: boolean;
}

/** One code box with its buttons, for the actions that need proof the person has their authenticator */
function CodeForm({ id, label, submitLabel, destructive, onSubmit, onCancel }: { id: string; label: string; submitLabel: string; destructive?: boolean; onSubmit: (code: string) => Promise<void>; onCancel: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(code.trim());
    } catch (err: any) {
      setError(err?.message || "That code is not right. Check the app and try again.");
      setCode("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-lg border border-border bg-muted/40 p-3.5">
      <Label htmlFor={id} className="text-[13px]">{label}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input id={id} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" autoFocus maxLength={16} placeholder="123 456" className="h-9 max-w-[180px] font-mono tracking-[0.18em]" aria-invalid={error ? true : undefined} />
        <Button type="submit" size="sm" variant={destructive ? "destructive" : "default"} disabled={busy || !code.trim()}>
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : submitLabel}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
      {error && <p role="alert" className="text-xs text-danger">{error}</p>}
    </form>
  );
}

/** The set-up steps with the same phone as on the sign-in set-up screen beside them, following the step */
function WithPhone({ stage, children }: { stage: number; children: React.ReactNode }) {
  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
      {children}
      <div className="relative hidden overflow-hidden rounded-xl bg-[#07075E] py-6 lg:block">
        <svg aria-hidden="true" viewBox="0 0 400 400" fill="none" stroke="#FFFFFF" strokeOpacity="0.07" className="pointer-events-none absolute -bottom-40 -right-40 size-[400px]">
          <circle cx="200" cy="200" r="90" />
          <circle cx="200" cy="200" r="140" />
          <circle cx="200" cy="200" r="190" />
        </svg>
        <div className="[zoom:0.85]">
          <PhoneVisual step={stage} />
        </div>
      </div>
    </div>
  );
}

/** Settings → Security: two-step verification with an authenticator app, on or off, backup codes */
export function TwoFactorCard() {
  const { refreshUser } = useAuth();
  const { success } = useToast();
  const [status, setStatus] = useState<Status | null>(null);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<"idle" | "setup" | "renew" | "move" | "off" | "codes">("idle");
  // Moving to a new phone: the QR code the server gave after the current phone or a backup code was accepted
  const [moving, setMoving] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [stage, setStage] = useState(0);

  const load = useCallback(async () => {
    try {
      setStatus(unwrapObject<Status>(await api.get("/api/auth/2fa")));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const changed = async () => {
    setMode("idle");
    await refreshUser();
    await load();
  };

  return (
    <section aria-labelledby="two-factor-heading" className="space-y-3 rounded-[10px] border border-border bg-card px-4 py-3.5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            {status?.enabled ? <ShieldCheck className="size-[18px] text-success" /> : <ShieldOff className="size-[18px]" />}
          </span>
          <div>
            <h2 id="two-factor-heading" className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
              Two-step verification
              {status && <Badge variant={status.enabled ? "success" : "neutral"} className="text-[10px] font-normal">{status.enabled ? "On" : "Off"}</Badge>}
            </h2>
            <p className="text-xs text-muted-foreground">
              Signing in also asks for a 6-digit code from an authenticator app on your phone, so a stolen password alone is not enough.
              {status?.required && !status.enabled && " Your company requires it."}
            </p>
            {status?.enabled && (
              <p className="mt-1 text-xs text-muted-foreground">
                Turned on {status.enabled_at ? formatDateTime(status.enabled_at) : ""} · {status.backup_codes_left} backup code{status.backup_codes_left === 1 ? "" : "s"} left
                {status.backup_codes_left <= 2 && <span className="font-medium text-warning"> · make new ones soon</span>}
              </p>
            )}
          </div>
        </div>
        {status && mode === "idle" && (
          <div className="flex flex-wrap gap-2">
            {!status.enabled ? (
              <Button size="sm" onClick={() => setMode("setup")}>
                <KeyRound className="mr-1.5 size-3.5" />
                Set up
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => setMode("move")}>
                  <Smartphone className="mr-1.5 size-3.5" />
                  Move to a new phone
                </Button>
                <Button size="sm" variant="outline" onClick={() => setMode("renew")}>New backup codes</Button>
                <Button size="sm" variant="outline" onClick={() => setMode("off")} disabled={status.required} title={status.required ? "Your company requires two-step verification for your role" : undefined}>
                  Turn off
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {failed && <p role="alert" className="text-sm text-danger">Could not load this. <button type="button" onClick={load} className="font-medium underline">Try again</button></p>}
      {!status && !failed && (
        <div role="status" className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      )}

      {mode === "setup" && (
        <WithPhone stage={stage}>
          <TwoFactorSetup
            onStageChange={setStage}
            onDone={async () => {
              success("Two-step verification is on", "You will be asked for a code when you sign in");
              await changed();
            }}
            onCancel={() => setMode("idle")}
          />
        </WithPhone>
      )}
      {mode === "move" && !moving && (
        <div className="space-y-2">
          {moveError && <p role="alert" className="text-xs text-danger">{moveError}</p>}
          <CodeForm
            id="move-code"
            label="First, prove it is you: enter a code from your current authenticator app, or one of your backup codes if you no longer have that phone."
            submitLabel="Continue"
            onCancel={() => {
              setMoveError(null);
              setMode("idle");
            }}
            onSubmit={async (code) => {
              setMoving(unwrapObject<{ secret: string; otpauth_uri: string }>(await api.post("/api/auth/2fa/replace/start", { code })));
              setMoveError(null);
              setStage(0);
            }}
          />
        </div>
      )}
      {mode === "move" && moving && (
        <WithPhone stage={stage}>
          <TwoFactorSetup
            initial={moving}
            confirmPath="/api/auth/2fa/replace/confirm"
            doneTitle="Your new phone is set up"
            onStageChange={setStage}
            onExpired={(message) => {
              setMoving(null);
              setMoveError(`${message} Enter a code again to get one.`);
            }}
            onDone={async () => {
              setMoving(null);
              success("Moved to your new phone", "Codes from the old phone and the old backup codes no longer work");
              await changed();
            }}
            onCancel={() => {
              setMoving(null);
              setMode("idle");
            }}
          />
        </WithPhone>
      )}
      {mode === "renew" && (
        <CodeForm
          id="renew-code"
          label="Enter a code from your authenticator app, or one of your backup codes, to make ten new backup codes. The old ones stop working."
          submitLabel="Make new codes"
          onCancel={() => setMode("idle")}
          onSubmit={async (code) => {
            const res = unwrapObject<{ backup_codes: string[] }>(await api.post("/api/auth/2fa/backup-codes", { code }));
            setCodes(res?.backup_codes ?? []);
            setMode("codes");
          }}
        />
      )}
      {mode === "codes" && <BackupCodes codes={codes} onDone={changed} />}
      {mode === "off" && (
        <CodeForm
          id="off-code"
          label="Enter a code from your authenticator app, or a backup code, to turn two-step verification off."
          submitLabel="Turn off"
          destructive
          onCancel={() => setMode("idle")}
          onSubmit={async (code) => {
            await api.post("/api/auth/2fa/disable", { code });
            success("Two-step verification is off", "Sign-in asks for your password only");
            await changed();
          }}
        />
      )}
    </section>
  );
}
